import React, { useContext, useEffect } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MessageIcon from "@mui/icons-material/Message";
import { ExpressionType, VotesConfigData } from "../Shared/Party/PartyConfigData";
import { StateManagerContext } from "./StateManagerContext";
import { StateManager } from "./PartyState";


export type RowOrColumn = "row" | "column";

export type VoteType = "impressions" | "likes" | "comments" | "likesWithExpression";

export const expressionColors = [
  "#4040ff",
  "#ff0000",
  "#d4d435",
  "#60bf60",
  "#d435d4",
  "#35d4d4",
];

export const expressionTexts = [
  "inspiring",
  "appreciative",
  "smart",
  "perceptive",
  "informative",
  "funny"
]

export type ExpressionProps = {
  expression?: ExpressionType;
  fontSize: number;
  translation: number;
}

export type VoteCounterProps = {
  voting: VotesConfigData;
  matchExpression: boolean;
  textFirst?: boolean;
  voteType: VoteType;
  fontBase: number;
  direction: RowOrColumn;
  justify: string;
  padding: number;
  iconPadding? : number;
  text?: string;
  clickHandler?(): void;
};

const s3URL =
  "/images/expressions/hearts/";
const outlinedExpressionURLs = [
  "inspiring-o-heart.png",
  "appreciative-o-heart.png",
  "smart-o-heart.png",
  "perceptive-o-heart.png",
  "informative-o-heart.png",
  "funny-o-heart.png",
];

const filledExpressionURLs = [
  "inspiring-f-heart.png",
  "appreciative-f-heart.png",
  "smart-f-heart.png",
  "perceptive-f-heart.png",
  "informative-f-heart.png",
  "funny-f-heart.png",
];


const getExpressionIndex = (exp?: ExpressionType): number => {
  const expression = exp ?? "undefined";
  const expressionIndex =
    expression === "undefined"
      ? -1
      : expression === "inspiring"
        ? 0
        : expression === "appreciative"
          ? 1
          : expression === "smart"
            ? 2
            : expression === "perceptive"
              ? 3
              : expression === "informative"
                ? 4
                : 5;
  return expressionIndex;

}


export const ExpressionText = ({ expression, fontSize, translation }: ExpressionProps) => {
  const expressionIndex = getExpressionIndex(expression);
  const expressionText =
    expressionIndex < 0
      ? "UNDEFINED"
      : "#" + expressionTexts[expressionIndex];
  const expressionColor =
    expressionIndex >= 0 ? expressionColors[expressionIndex] : 'white';


  return (
    <span
      style={{ fontSize: fontSize, translate: translation + 'px', color: expressionColor }}
    >
      {expressionText}
    </span>
  );
}


export const VoteCounterDisplay = ({ voting, matchExpression, voteType, fontBase, direction, justify, padding, clickHandler, text, textFirst, iconPadding }: VoteCounterProps) => {
  const itemId = React.useRef<string>(voting.itemId);

  if(voting.userLiked === undefined)
    voting.userLiked = false;

  const userLikedRef = React.useRef<boolean>(voting.userLiked ?? false);
  const transitioning = React.useRef<boolean>(false);
  const visible = React.useRef<boolean>(true);
  const rcRef = React.useRef<number>(0);
  const [rc, setRC] = React.useState<number>(0);

  const likesListener = React.useRef<number>(-1);

  const stateManager = React.useContext(StateManagerContext);

  if (likesListener.current === -1) {
    likesListener.current = Math.random();
    stateManager.addTickListener(likesListener.current, (sm: StateManager, time: number) => {
      rcRef.current = rcRef.current + 1;
      setRC(rcRef.current);
    });
  }

  useEffect(() => { return () => {  stateManager.removeTickListener(likesListener.current) } })

  const click = () => {
    if (clickHandler !== undefined) {
      clickHandler();
      if ((voting.userLiked !== userLikedRef.current) && ((voteType === 'likes') || (voteType == 'likesWithExpression')) && !(transitioning.current)) {
        transitioning.current = true;
        visible.current = false;
        rcRef.current = rcRef.current + 1;
      setRC(rcRef.current);
        window.setInterval(() => {
          userLikedRef.current = voting.userLiked ?? false;
          visible.current = true;
          transitioning.current = false;
          rcRef.current = rcRef.current + 1;
      setRC(rcRef.current);
        },
          500);
      }

    }
  };

  if (voting.itemId !== itemId.current) {

    itemId.current = voting.itemId;
    userLikedRef.current = voting.userLiked ?? false;
  }

  const count = voteType === 'impressions' ? voting.impressions : (voteType === 'likes' ? voting.likes : voting.comments);
 

  const displayCount = (((voteType === 'likes') || (voteType === 'likesWithExpression')) ? (userLikedRef.current ? 1 : 0) : 0) + count;
  //console.log('Voting Count Display : item ' + voting.itemId + ' count : ' + count + ' displayCount : ' + displayCount + ' rc : ' + rcRef.current);

  var icon: React.ReactElement = <VisibilityIcon sx={{ maxHeight: 1.5 * fontBase + "px" }} />
  if ((voteType === 'likes') || (voteType === 'likesWithExpression')) {
    const expressionIndex = getExpressionIndex(voting.expression);

    const expressionURL = s3URL + (
      ((expressionIndex < 0) || (!matchExpression)) ? (userLikedRef.current ? "neutral-f-heart.png" : "neutral-o-heart.png") : (userLikedRef.current ? filledExpressionURLs[expressionIndex] : outlinedExpressionURLs[expressionIndex]));

    icon =
      (
        <div
          style={{
            maxHeight: (2 * fontBase - 4) + "px",
            minHeight: (2 * fontBase - 4) + "px",
            minWidth: 1.5 * fontBase + "px",
            //backgroundColor: "black",
            backgroundImage: `url(${expressionURL})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPosition: "center",
            //backgroundColor : "cyan",
            color: "white",
          }}
          onClick={() => { if (!transitioning.current) click(); }}
        >
        </div>
      );

  }
  else {
    icon = <MessageIcon sx={{ maxHeight: 1.5 * fontBase + "px" }} onClick={() => { if (!transitioning.current) click(); }} />
  }

  const txtF = (text !== undefined) && (textFirst ?? false);
  const exF = (voteType === 'likesWithExpression') && (textFirst ?? false);
  const txtE = (text !== undefined) && (!(textFirst ?? false));
  const exE = (voteType === 'likesWithExpression') && (!(textFirst ?? false));
  const textToDisplay = (text ?? '');
  const expressionProps: ExpressionProps = {
    expression: voting.expression,
    fontSize: fontBase,
    translation: 0
  }



  return (
    <div style={{ justifyContent: justify, display: "flex", flexDirection: direction }} onClick={() => { click(); }}>
      {
        txtF && <span style={{ fontSize: fontBase + "px" }}>{textToDisplay}</span>
      }
      {
        exF && <ExpressionText {...expressionProps} />
      }
      <div style={{
        opacity: visible.current ? 1.0 : 0.0,
        transition: 'opacity 0.5s',
        transitionTimingFunction: 'ease-in-out',
        paddingLeft : txtF ? padding + 'px' : '0px'
      }}
        onClick={() => { if (!transitioning.current) click(); }}>
        {icon}
      </div>

      <span style={{ fontSize: fontBase + "px", paddingLeft: (iconPadding ?? 5) + "px" }}
        onClick={() => { click(); }}>
        {Math.floor(displayCount)}
      </span>
      {
        txtE && <span style={{ fontSize: fontBase + "px", paddingLeft: padding + "px" }}>{textToDisplay}</span>
      }
      {
        exE && <ExpressionText {...expressionProps} />
      }
    </div>
  );
}
